import { useCallback, useMemo, useState } from 'react';

import { useCrmObject } from '@/lib/crm/store';

import type { EditingTeam } from './useTeamSave';

const EMPTY_IDS: readonly string[] = [];

interface UseTeamMemberSelectionParams {
  isOpen: boolean;
  editingTeam: EditingTeam | null | undefined;
  orgId: number;
}

interface UseTeamMemberSelectionResult {
  selectedUserIds: Set<string>;
  initialMemberUserIds: readonly string[];
  isMemberIdsLoading: boolean;
  isMemberIdsError: boolean;
  handleToggleMember: (entityId: string) => void;
}

/**
 * Owns the team-member-picker state: the current selection, and the baseline
 * taken from the team's existing `ahoy_team_to_user` associations (edit mode)
 * or an empty baseline (create mode).
 *
 * The baseline is read through the CRM object store (`useCrmObject`), so it
 * paints instantly on reopen and follows WebSocket updates. The user's
 * toggles are held as an overlay on top of that baseline: until they touch a
 * checkbox the selection *is* the baseline, and once they do, a baseline
 * refresh no longer wipes their half-made changes — it only moves the point
 * the save diff is computed against.
 */
export function useTeamMemberSelection({
  isOpen,
  editingTeam,
  orgId,
}: UseTeamMemberSelectionParams): UseTeamMemberSelectionResult {
  const entityId = editingTeam?.entity_id;

  const { object, isLoading, error } = useCrmObject(
    orgId,
    'ahoy_team',
    entityId,
    { includeAssociations: true, enabled: isOpen && Boolean(entityId) }
  );

  const associations = object?.associations;
  const initialMemberUserIds = useMemo<readonly string[]>(() => {
    if (!entityId) {
      return EMPTY_IDS;
    }
    return (
      associations
        ?.filter(a => a.association_type_name === 'ahoy_team_to_user')
        .map(a => a.target_entity_id) ?? EMPTY_IDS
    );
  }, [entityId, associations]);

  // User edits, or null while the selection still mirrors the baseline.
  const [edits, setEdits] = useState<Set<string> | null>(null);

  // Drop any edits when the slideout opens or the team changes, so the form
  // rebuilds from the saved membership. This is the "adjust state during
  // render" form of a reset — compare the previous props, not an effect.
  const [prevOpen, setPrevOpen] = useState(isOpen);
  const [prevEntityId, setPrevEntityId] = useState(entityId);
  if (isOpen !== prevOpen || entityId !== prevEntityId) {
    setPrevOpen(isOpen);
    setPrevEntityId(entityId);
    setEdits(null);
  }

  const selectedUserIds = useMemo(
    () => edits ?? new Set(initialMemberUserIds),
    [edits, initialMemberUserIds]
  );

  const handleToggleMember = useCallback(
    (toggledId: string) => {
      setEdits(prev => {
        const next = new Set(prev ?? initialMemberUserIds);
        if (next.has(toggledId)) {
          next.delete(toggledId);
        } else {
          next.add(toggledId);
        }
        return next;
      });
    },
    [initialMemberUserIds]
  );

  // A store entry seeded by a lean fetch elsewhere (e.g. the teams list) has
  // no `associations` yet — treat that the same as "still loading".
  const isMemberIdsLoading =
    Boolean(entityId) && !error && (isLoading || associations === undefined);

  return {
    selectedUserIds,
    initialMemberUserIds,
    isMemberIdsLoading,
    isMemberIdsError: Boolean(error),
    handleToggleMember,
  };
}
